/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { __ } from '@web-stories-wp/i18n';
import { generatePatternStyles } from '@web-stories-wp/patterns';
import {
  FULLBLEED_HEIGHT,
  PAGE_WIDTH,
  SAFE_AREA_HEIGHT,
} from '@web-stories-wp/units';
import { getTotalDuration, StoryAnimation } from '@web-stories-wp/animation';
/**
 * Internal dependencies
 */
import StoryPropTypes from '../types';
import { elementIs } from '../elements';
import isElementBelowLimit from '../utils/isElementBelowLimit';
import OutputElement from './element';
import getLongestMediaElement from './utils/getLongestMediaElement';
import { pageToCanvasPage } from '../../../../utils/pageToCanvasPage';
import {
  getOverlayFilterStyle,
  getOverlayRenderStack,
} from '../utils/overlayStack';
import normalizePageElements from './normalizePageElements';

const ASPECT_RATIO = `${PAGE_WIDTH}:${SAFE_AREA_HEIGHT}`;

function OutputPage({ page: _page, autoAdvance, defaultPageDuration }) {
  // Ensure that existing and new pages don't use the safe zone offset for consistency
  const canvasPage = pageToCanvasPage(_page);
  const page = {
    ...canvasPage,
    elements: normalizePageElements(canvasPage.elements),
  };
  const { id, animations, elements, backgroundColor, backgroundAudio } = page;

  const [firstElement, ...remainingElements] = elements;
  const backgroundElement =
    firstElement &&
    elementIs.backgroundable(firstElement) &&
    firstElement.isBackground
      ? firstElement
      : undefined;
  const regularElements = backgroundElement ? remainingElements : elements;

  // If the background element has base color set, it's media, use that.
  const baseColor =
    backgroundElement && elementIs.media(backgroundElement)
      ? backgroundElement?.resource?.baseColor
      : undefined;
  const backgroundStyles = baseColor
    ? {
        backgroundColor: `rgb(${baseColor[0]},${baseColor[1]},${baseColor[2]})`,
      }
    : {
        backgroundColor: 'white',
        ...generatePatternStyles(backgroundColor, 'background', null, {
          width: PAGE_WIDTH,
          height: FULLBLEED_HEIGHT,
        }),
      };

  const animationDuration = getTotalDuration({ animations }) / 1000;
  // If the page doesn't have media, take either the animations time or the configured default duration time.
  const nonMediaPageDuration = Math.max(
    animationDuration || 0,
    defaultPageDuration
  );
  // If we have media, take the media time for advancement time and ignore the default,
  // but still consider animation time as the minimum, too.
  const longestMediaElement = getLongestMediaElement(
    elements,
    animationDuration || 1
  );
  const autoAdvanceAfter = longestMediaElement?.id
    ? `el-${longestMediaElement?.id}-media`
    : `${nonMediaPageDuration}s`;

  const hasPageAttachment = page.pageAttachment?.url?.length > 0;

  // Remove invalid links, @todo this should come from the pre-publish checklist in the future.
  const validElements = regularElements.map((element) =>
    !hasPageAttachment || !isElementBelowLimit(element)
      ? element
      : {
          ...element,
          link: null,
        }
  );

  const getAnimationsForElement = (elementId) =>
    page.cssAnimations?.filter(
      (cssAnimation) => cssAnimation.target === elementId
    ) || [];
  const backgroundOverlayFilters = getOverlayRenderStack(backgroundElement);

  return (
    <amp-story-page
      id={id}
      auto-advance-after={autoAdvance ? autoAdvanceAfter : undefined}
      background-audio={backgroundAudio?.src ?? undefined}
    >
      <StoryAnimation.Provider
        animations={animations}
        elements={elements}
        useCssAnimations={true}
      >
        {backgroundElement && (
          <amp-story-grid-layer
            template="vertical"
            aspect-ratio={ASPECT_RATIO}
            class="grid-layer"
          >
            <div className="page-fullbleed-area" style={backgroundStyles}>
              <div className="page-safe-area">
                <OutputElement
                  animations={getAnimationsForElement(backgroundElement.id)}
                  element={backgroundElement}
                  isPageNormalized
                />
                {backgroundOverlayFilters.map((filter) => (
                  <div key={filter.id} className="page-background-overlay-area">
                    <div
                      className="page-background-overlay-layer"
                      style={getOverlayFilterStyle(filter, {
                        height: FULLBLEED_HEIGHT,
                        width: PAGE_WIDTH,
                      })}
                    />
                  </div>
                ))}
              </div>
            </div>
          </amp-story-grid-layer>
        )}

        <amp-story-grid-layer
          template="vertical"
          aspect-ratio={ASPECT_RATIO}
          class="grid-layer"
        >
          <div
            className="page-fullbleed-area"
            style={!backgroundElement ? backgroundStyles : undefined}
          >
            <div className="page-safe-area">
              {validElements.map((element) => (
                <OutputElement
                  key={element.id}
                  animations={getAnimationsForElement(element.id)}
                  element={element}
                  isPageNormalized
                />
              ))}
            </div>
          </div>
        </amp-story-grid-layer>
      </StoryAnimation.Provider>
      {hasPageAttachment && (
        <amp-story-page-outlink
          layout="nodisplay"
          cta-image={page.pageAttachment.icon}
          theme={page.pageAttachment.theme}
        >
          <a href={page.pageAttachment.url}>
            {page.pageAttachment.ctaText || __('Learn more', 'web-stories')}
          </a>
        </amp-story-page-outlink>
      )}
    </amp-story-page>
  );
}

OutputPage.propTypes = {
  page: StoryPropTypes.page.isRequired,
  autoAdvance: PropTypes.bool,
  defaultPageDuration: PropTypes.number,
};

OutputPage.defaultProps = {
  autoAdvance: true,
  defaultPageDuration: 7,
};

export default OutputPage;
